import type { ApolloError } from "@qw/graphql";
import type { GraphQLAuthError } from "../types";

export const StatusCodes = {
  BadRequest: 400,
  Unauthorized: 401,
  Forbidden: 403,
  NotFound: 404,
  RequestTimeout: 408,
  TooManyRequests: 429,
  InternalServerError: 500,
  ServiceUnavailable: 503,
  isServer: (status: number) => status >= 500 && status < 600,
  isClient: (status: number) => status >= 400 && status < 500,
};

/**
 * Type guard for Apollo Client errors
 */
export function isApolloError(err: unknown): err is ApolloError {
  return err instanceof Error && "graphQLErrors" in err && Array.isArray((err as ApolloError).graphQLErrors);
}

/**
 * Checks if an Apollo Client error contains a GraphQL error with the specified auth status code.
 * This is useful for checking specific error conditions like NotFound, Unauthorized, etc.
 */
export function hasAuthErrorWithStatus(error: unknown, statusCode: number): boolean {
  if (!isApolloError(error)) {
    return false;
  }

  return error.graphQLErrors.some((graphQLError) => {
    const authError = (graphQLError as GraphQLAuthError).extensions?.authError;
    return authError?.statusCode === statusCode;
  });
}

type AuthErrorExtensions = GraphQLAuthError["extensions"] | undefined;

/** The three extension shapes the API can serialize a status into, checked as one. */
const extensionsHaveStatus = (extensions: AuthErrorExtensions, statusCode: number): boolean =>
  extensions?.authError?.statusCode === statusCode ||
  extensions?.response?.statusCode === statusCode ||
  Number(extensions?.code) === statusCode;

export function hasErrorWithStatus(error: unknown, statusCode: number): boolean {
  if (!isApolloError(error)) {
    return false;
  }

  const networkError = error.networkError as
    | {
        statusCode?: number;
        status?: number;
      }
    | undefined;

  if (networkError?.statusCode === statusCode || networkError?.status === statusCode) {
    return true;
  }

  return error.graphQLErrors.some((graphQLError) =>
    extensionsHaveStatus((graphQLError as GraphQLAuthError).extensions, statusCode)
  );
}

/**
 * Which of the two failures an auth-gating request hit.
 *
 * The repo used to conflate these: "you are signed out" and "we could not reach the server right
 * now" both meant "go to login", so one shed request could bounce a user holding a valid token to
 * a bare login form (B-1963). Only `signed-out` may clear the stored credential; `unreachable`
 * leaves the token untested and is worth retrying. `other` is a real error to show, not a reason
 * to sign anyone out.
 */
export type AuthFailureKind = "signed-out" | "unreachable" | "other";

/** What `AppAuthGuard`'s `UnauthorizedException` serializes to. Arrives on an HTTP 200 body. */
const UNAUTHENTICATED_CODE = "UNAUTHENTICATED";

interface NetworkErrorShape {
  name?: string;
  status?: number;
  statusCode?: number;
  result?: unknown;
}

const networkErrorOf = (error: ApolloError): NetworkErrorShape | null =>
  (error.networkError as NetworkErrorShape | null) ?? null;

const hasName = (value: unknown, name: string): boolean =>
  typeof value === "object" && value !== null && (value as { name?: unknown }).name === name;

const hasUnauthenticatedCode = (graphQLError: unknown): boolean =>
  (graphQLError as GraphQLAuthError | undefined)?.extensions?.code === UNAUTHENTICATED_CODE;

/**
 * The GraphQL errors carried in a *non-2xx* response body. Apollo throws `ServerError` for those
 * and parks the parsed body on `networkError.result`, leaving `graphQLErrors` empty — so a
 * sign-out delivered that way is invisible to the `graphQLErrors` checks above. The API returns
 * 200 for auth failures today, so this is unreachable in production; reading it means a future
 * gateway status change cannot silently turn a sign-out into an outage. `result` is typed
 * `Record<string, any> | string` because a plain-text body lands here verbatim.
 */
const bodyGraphQLErrors = (networkError: NetworkErrorShape | null): readonly unknown[] => {
  const result = networkError?.result;

  if (!result || typeof result !== "object") {
    return [];
  }

  const errors = (result as { errors?: unknown }).errors;
  return Array.isArray(errors) ? errors : [];
};

const isSignedOut = (error: unknown): boolean => {
  if (hasErrorWithStatus(error, StatusCodes.Unauthorized)) {
    return true;
  }

  if (!isApolloError(error)) {
    return false;
  }

  if (error.graphQLErrors.some(hasUnauthenticatedCode)) {
    return true;
  }

  return bodyGraphQLErrors(networkErrorOf(error)).some(
    (graphQLError) =>
      hasUnauthenticatedCode(graphQLError) ||
      extensionsHaveStatus((graphQLError as GraphQLAuthError | undefined)?.extensions, StatusCodes.Unauthorized)
  );
};

const isUnreachable = (error: unknown): boolean => {
  // A navigation abort or the `timeoutLink`'s `AbortController`, either raised directly or wrapped.
  if (hasName(error, "AbortError")) {
    return true;
  }

  if (!isApolloError(error)) {
    // Non-Apollo call sites: `authenticatedFetch`, or a raw `fetch`. A rejected fetch is a
    // `TypeError` in every browser, with a message that differs per browser — hence the type check
    // rather than a message match.
    return error instanceof TypeError;
  }

  const networkError = networkErrorOf(error);

  if (!networkError) {
    return false;
  }

  if (hasName(networkError, "AbortError")) {
    return true;
  }

  // A 2xx whose body would not parse. We were answered by something, but not by the API.
  if (networkError.name === "ServerParseError") {
    return true;
  }

  const status = networkError.statusCode ?? networkError.status;

  // The fetch itself rejected, so there is no status to read. Classified by that absence rather
  // than by matching "Failed to fetch" / "Load failed" / "NetworkError when attempting to fetch
  // resource." — the message is per-browser and any list of them is a list that misses one.
  if (status === undefined) {
    return true;
  }

  return (
    status === StatusCodes.RequestTimeout || status === StatusCodes.TooManyRequests || StatusCodes.isServer(status)
  );
};

/**
 * Key `@qw/graphql`'s annotation link stamps the access token a failing operation actually sent
 * onto — on `networkError`, and on each `graphQLErrors[].extensions` for the HTTP-200 transport.
 * Duplicated as a literal rather than imported so this module stays free of a `@qw/graphql` value
 * import; the constant is exported from there and the two are asserted equal in that package's
 * tests.
 */
const AUTH_TOKEN_USED_KEY = "authTokenUsed";

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null ? (value as unknown as Record<string, unknown>) : undefined;

/**
 * The credential a failure belongs to, if the annotation link recorded one.
 *
 * `null` means "not annotated" — no client-side link chain, a hand-built error in a test, a
 * non-Apollo throw — and every caller treats that as "cannot tell", leaving behavior unchanged.
 */
const readTokenUsed = (error: unknown): { tokenUsed: string | undefined } | null => {
  if (!isApolloError(error)) {
    return null;
  }

  const networkError = asRecord(error.networkError);

  if (networkError && AUTH_TOKEN_USED_KEY in networkError) {
    return { tokenUsed: networkError[AUTH_TOKEN_USED_KEY] as string | undefined };
  }

  for (const graphQLError of error.graphQLErrors) {
    const extensions = asRecord(graphQLError.extensions);

    if (extensions && AUTH_TOKEN_USED_KEY in extensions) {
      return { tokenUsed: extensions[AUTH_TOKEN_USED_KEY] as string | undefined };
    }
  }

  return null;
};

export interface ClassifyAuthFailureOptions {
  /**
   * The access token in storage right now. Supply it and a sign-out is only reported when the
   * failure belongs to *this* credential; omit it and classification is unchanged.
   */
  currentToken?: string;
}

/**
 * Evaluated sign-out first: a rejected credential outranks an unreachable server if a response
 * somehow looks like both, because continuing to hold a rejected token is the worse mistake.
 *
 * A sign-out is downgraded to `unreachable` when the failure demonstrably belongs to a credential
 * that has already been replaced. This is the same defect the error link's staleness check covers,
 * one layer up and with worse consequences: the link only decides whether to call
 * `onUnauthorized`, but a caller acting on `"signed-out"` clears `authDataAtom` — so a 401 for a
 * token another tab already replaced would destroy the *new* credential. Only the two authorities
 * sharing one signal closes it, and the brokerage app has no error link at all.
 *
 * `unreachable` rather than a fourth kind: it is the reading that produces the right behavior
 * everywhere without a ripple — retry once with the credential now in storage, which resolves the
 * situation on its own, and never touch `authDataAtom`. A new kind would fall through to a generic
 * error page in both Next.js providers and need a telemetry mapping for no gain.
 */
export function classifyAuthFailure(error: unknown, options?: ClassifyAuthFailureOptions): AuthFailureKind {
  if (!error) {
    return "other";
  }

  if (isSignedOut(error)) {
    const annotation = options && "currentToken" in options ? readTokenUsed(error) : null;

    if (annotation && annotation.tokenUsed !== options?.currentToken) {
      return "unreachable";
    }

    return "signed-out";
  }

  if (isUnreachable(error)) {
    return "unreachable";
  }

  return "other";
}

export function humanReadableStatus(status: number): string {
  switch (status) {
    case StatusCodes.BadRequest:
      return "Bad Request";
    case StatusCodes.Unauthorized:
      return "Unauthorized";
    case StatusCodes.Forbidden:
      return "Forbidden";
    case StatusCodes.NotFound:
      return "Not Found";
    case StatusCodes.RequestTimeout:
      return "Request Timeout";
    case StatusCodes.TooManyRequests:
      return "Too Many Requests";
    case StatusCodes.ServiceUnavailable:
      return "Service Unavailable";
    default:
      if (StatusCodes.isServer(status)) {
        return "Internal Server Error";
      }
      return "Unknown";
  }
}
